import {
  Alert,
  AlertColor,
  Slide,
  Snackbar,
  SnackbarCloseReason,
  SnackbarProps,
} from '@mui/material';
import { createContext, FC, ReactNode, useContext, useState } from 'react';

type SnackBarContextActions = {
  showSnackBar: (
    text: SnackbarProps['message'],
    color: AlertColor,
    time?: SnackbarProps['autoHideDuration']
  ) => void;
};

const SnackBarContext = createContext({} as SnackBarContextActions);

interface SnackBarContextProviderProps {
  children: ReactNode;
}

const SnackBarProvider: FC<SnackBarContextProviderProps> = ({ children }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<SnackbarProps['message']>('');
  const [duration, setDuration] =
    useState<SnackbarProps['autoHideDuration']>(2500);
  const [severity, setSeverity] = useState<AlertColor>('info');

  const showSnackBar = (
    text: SnackbarProps['message'],
    color: AlertColor,
    time: SnackbarProps['autoHideDuration'] = 2500
  ) => {
    setMessage(text);
    setSeverity(color);
    setDuration(time);
    setOpen(true);
  };

  const handleClose = (
    event: Event | React.SyntheticEvent<any, Event>,
    reason?: SnackbarCloseReason
  ) => {
    if (reason && reason === 'clickaway') {
      return;
    }

    setOpen(false);
    setSeverity('info');
  };

  return (
    <SnackBarContext.Provider value={{ showSnackBar }}>
      <Snackbar
        open={open}
        autoHideDuration={duration}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={handleClose}
        TransitionComponent={Slide}
        sx={{ right: 16, bottom: 96, left: 16 }}
      >
        <Alert onClose={handleClose} severity={severity} className='w-full'>
          {message}
        </Alert>
      </Snackbar>
      {children}
    </SnackBarContext.Provider>
  );
};

const useSnackBar = (): SnackBarContextActions => {
  const context = useContext(SnackBarContext);

  if (!context) {
    throw new Error('useSnackBar must be used within an SnackBarProvider');
  }

  return context;
};

export { SnackBarProvider, useSnackBar };
